/**
 * Group citizenship ceremony capacity.
 *
 * `GET {blApi}/citizenship/availability/{company}/{service}/{date}` answers one question
 * only: how many places are left on that ceremony — the service's
 * `citizenship_group_max` (or the per-date `citizenship_overrides_{client}` value) minus
 * the rows already in `citizenship_claims_{client}` for that datetime. It is never told
 * how many people this booking is for, so it can report capacity but never police it.
 *
 * The party-size comparison therefore belongs to the journey, and it has to be made the
 * same way at every point the customer can commit to a slot: the calendar when they pick
 * a time, and the summary page before the pre-basket call hands them to the payment
 * gateway. Both read this file so the two cannot drift apart.
 */

/** The form-builder field holding the attendee count for a group ceremony. */
export const CITIZENSHIP_ATTENDEES_FIELD =
	'how_many_attendees_and_their_details_including_if_adult_or_child';

export interface CitizenshipPartySize {
	/** Places this booking will take on the ceremony. Never less than one. */
	places: number;
	/** The question was answered, but not with a number we can use. */
	unusableAnswer: boolean;
}

/**
 * The attendee answer for the selected schema.
 *
 * The form-builder keeps its own answers in sessionStorage rather than in the datastore,
 * keyed by schema id, which is why this is read straight from storage on every page that
 * needs it.
 */
export function citizenshipAttendeesAnswer(selectedSchema: any): any {
	let answers: any;
	try {
		answers = JSON.parse(sessionStorage.getItem('form_builder_answers') || '{}');
	} catch (err) {
		console.error('form_builder_answers could not be parsed', err);
		return undefined;
	}
	return answers?.[selectedSchema]?.[CITIZENSHIP_ATTENDEES_FIELD];
}

/**
 * How many places on the ceremony this booking will consume.
 *
 * "Myself" bookings never display the attendees question, so a missing or blank answer
 * means a party of one rather than something that blocks the journey. An answer that is
 * there but unusable is reported separately: the calendar can legitimately send the
 * customer back to the question, while the summary page — where the details are already
 * behind them — must not turn a malformed answer into a refused payment.
 *
 * The answer is stored as the raw scalar the customer gave, but it is unwrapped from a
 * `{ value }` shape as well, so a form-builder that starts wrapping answers cannot
 * silently reduce every party to one person.
 */
export function citizenshipPartySize(attendeesAnswer: any): CitizenshipPartySize {
	const raw =
		attendeesAnswer && typeof attendeesAnswer === 'object' && 'value' in attendeesAnswer
			? attendeesAnswer.value
			: attendeesAnswer;

	const answered = raw !== undefined && raw !== null && `${raw}`.trim() !== '';
	if (!answered) {
		return { places: 1, unusableAnswer: false };
	}

	const parsed = parseInt(raw, 10);
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return { places: 1, unusableAnswer: true };
	}

	return { places: parsed, unusableAnswer: false };
}

/** Whether the party fits in what citizenshipAvailability reported. */
export function hasCapacityForParty(availability: any, requiredPlaces: number): boolean {
	const slots = Number(availability?.slots);
	if (!Number.isFinite(slots) || slots <= 0) {
		return false;
	}
	return requiredPlaces <= slots;
}

/** The slot count to show the customer, for an availability response of any shape. */
export function availableSlotsForDisplay(availability: any): number {
	const slots = Number(availability?.slots);
	return Number.isFinite(slots) ? slots : 0;
}

/**
 * The three answers a capacity check can give.
 *
 * `api.service.citizenshipAvailability` swallows every failure and returns the error
 * *message string*, so an unreachable endpoint arrives here looking exactly like a
 * response with no places left. That distinction does not matter on the calendar, where
 * the customer can simply pick a different time, but it matters a great deal on the
 * summary page: telling someone their ceremony is full when all that happened was a
 * failed request would send them away from a booking they could have completed.
 */
export type CitizenshipCapacityOutcome = 'available' | 'full' | 'unknown';

/**
 * Whether the party still fits, and whether we were actually told.
 *
 * 'unknown' means the endpoint gave us nothing we can compare against — the caller
 * should say nothing to the customer rather than invent a capacity problem.
 */
export function citizenshipCapacityOutcome(
	availability: any,
	requiredPlaces: number
): CitizenshipCapacityOutcome {
	if (!availability || typeof availability !== 'object') {
		return 'unknown';
	}
	if (!Number.isFinite(Number(availability.slots))) {
		return 'unknown';
	}
	return hasCapacityForParty(availability, requiredPlaces) ? 'available' : 'full';
}

/**
 * The ceremony fee rows after this slot selection.
 *
 * The ceremony fee is the row with no question id, which is how summary and verify-payment
 * pick it out too. It is replaced rather than added to, because the customer can pick
 * another slot or change the ceremony type. A row is always produced, even at zero, because
 * both of those pages read the row without checking that it is there.
 *
 * A price that is not a number means the fetch failed: api.service returns the error message
 * as a string. That charges nothing, which is what the old code did as well.
 */
export function citizenshipFeeRows(
	serviceFees: any[],
	ceremonyFee: { item: string; price: any; places: number }
): any[] {
	const rows = (serviceFees || []).filter((fee: any) => fee.questionid !== undefined);
	const price = Number(ceremonyFee.price);
	const cost = Number.isFinite(price) ? price : 0;
	rows.push({
		questionid: undefined,
		item: ceremonyFee.item,
		quantity: ceremonyFee.places,
		cost,
		deposit: cost,
	});
	return rows;
}

/**
 * What the ceremony fee charges: the price for one citizen times the party size.
 *
 * The priced booking questions are already totalled this way. The ceremony row was read as
 * `cost` alone, so a party of two was shown two fees and charged for one.
 */
export function ceremonyFeeTotal(fee: any): number {
	const cost = Number(fee?.cost);
	if (!Number.isFinite(cost)) {
		return 0;
	}
	const quantity = parseInt(fee?.quantity, 10);
	return cost * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
}
